import { useEffect } from 'react'

import { useFormContext } from 'react-hook-form'

import { useSimulacaoContext } from '@/features/simulador/brasil/context/simulacao.context'
import type { TGetConfiguracoesPrecoResponse } from '@/features/simulador/brasil/http'
import type { TSimuladorSchemaInput } from '@/features/simulador/brasil/types/simulacao.type'

/**
 * Mantém os campos derivados da condição de preço alinhados com a configuração
 * vigente do produto, bloco e tipo de amortização da própria simulação.
 *
 * Reescreve só os quatro campos derivados, e não `recalcular` da factory, porque
 * `recalcular` reconstrói os grupos de parcelas e descartaria os valores já
 * digitados pelo vendedor.
 */
export const useSincronizarCondicaoDePreco = (
  configuracoesPreco: TGetConfiguracoesPrecoResponse['configuracoesPreco'],
) => {
  const { getValues, setValue } = useFormContext<TSimuladorSchemaInput>()
  const { indexEmpreendimento, indexSimulacao } = useSimulacaoContext()

  const simulacaoPath = `empreendimentos.${indexEmpreendimento}.simulacoes.${indexSimulacao}.simulacao` as const

  const { condicoesDePreco, tabelaDePreco } = configuracoesPreco

  useEffect(() => {
    const simulacaoAtual = getValues(simulacaoPath)
    if (!simulacaoAtual) return

    const camposAtualizados = {
      condicaoDePrecoId: condicoesDePreco.id,
      tabelaDePrecoId: tabelaDePreco.id,
      dataSaldoCondicaoPreco: condicoesDePreco.dataSaldo,
      dataPrevisaoDeEntregaDoBloco: condicoesDePreco.previsaoDeEntregaDoBloco,
    }

    const estaSincronizada = Object.entries(camposAtualizados).every(
      ([campo, valor]) => simulacaoAtual[campo as keyof typeof camposAtualizados] === valor,
    )
    if (estaSincronizada) return

    setValue(simulacaoPath, { ...simulacaoAtual, ...camposAtualizados })
  }, [
    condicoesDePreco.id,
    condicoesDePreco.dataSaldo,
    condicoesDePreco.previsaoDeEntregaDoBloco,
    tabelaDePreco.id,
    simulacaoPath,
    getValues,
    setValue,
  ])
}
